export interface TranscriptCue {
  speaker: string;
  text: string;
  startTime: string;
  endTime: string;
}

const TIME_RANGE_PATTERN =
  /(\d{2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2}\.\d{3})/g;

const SPEAKER_PATTERN = /^([^:]{1,80}):\s*(.*)$/s;

const stripMilliseconds = (time: string): string =>
  time.replace(/\.\d{3}$/, "");

const parseSpeakerText = (
  rawText: string,
): Pick<TranscriptCue, "speaker" | "text"> => {
  const normalizedText = rawText.replace(/\s+/g, " ").trim();
  if (!normalizedText) {
    return { speaker: "Unknown", text: "" };
  }

  const speakerMatch = SPEAKER_PATTERN.exec(normalizedText);
  if (speakerMatch) {
    return {
      speaker: speakerMatch[1].trim(),
      text: speakerMatch[2].trim(),
    };
  }

  return { speaker: "Unknown", text: normalizedText };
};

const extractTimeRanges = (
  content: string,
): {
  startTime: string;
  endTime: string;
  index: number;
  length: number;
}[] => {
  const ranges: {
    startTime: string;
    endTime: string;
    index: number;
    length: number;
  }[] = [];

  for (const match of content.matchAll(TIME_RANGE_PATTERN)) {
    if (match.index == null || !match[1] || !match[2]) continue;

    ranges.push({
      startTime: match[1],
      endTime: match[2],
      index: match.index,
      length: match[0].length,
    });
  }

  return ranges;
};

export const parseZoomVtt = (content: string): TranscriptCue[] => {
  const normalized = content
    .replace(/^\uFEFF/, "")
    .replace(/^WEBVTT[^\n]*\n*/i, "")
    .trim();
  if (!normalized) return [];

  const ranges = extractTimeRanges(normalized);
  if (!ranges.length) return [];

  const cues: TranscriptCue[] = [];

  for (let index = 0; index < ranges.length; index += 1) {
    const range = ranges[index];
    const textStart = range.index + range.length;
    const textEnd =
      index + 1 < ranges.length ? ranges[index + 1].index : normalized.length;
    const rawText = normalized.slice(textStart, textEnd).trim();
    const { speaker, text } = parseSpeakerText(rawText);

    if (!text) continue;

    cues.push({
      speaker,
      text,
      startTime: stripMilliseconds(range.startTime),
      endTime: stripMilliseconds(range.endTime),
    });
  }

  return cues;
};
